/** @jsxImportSource @emotion/react */
import { useState, useContext } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { PlainCard } from 'components/cards';
import { dateTimeParser } from 'helpers';
import { Icon, ShortText, Button } from 'components/common';
import colors from 'config/color';
import NotificationContext from 'contexts/notificationContext';
import api from 'api';

import * as styles from './AttestationSection.styles';

const AttestationSection = ({
  seekerAttestation,
  setModalSendAttestation,
  handleSelectEditAttestation,
  fetchSeekerAttestation,
}) => {
  const { candidateId } = useParams();
  const { setNotification, setNotificationContent } = useContext(NotificationContext);
  const [resendingId, setResendingId] = useState(null);

  const handleCopyLink = (link) => {
    navigator.clipboard.writeText(link);
    setNotification(true);
    setNotificationContent({ type: 'success', message: 'Link copied to clipboard' });
  };

  const handleOpenLink = (link) => {
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  const handleResendEmail = async (attestationId) => {
    setResendingId(attestationId);
    try {
      const { data } = await api.resendEmailAttestation(candidateId, attestationId);
      await fetchSeekerAttestation();
      setNotification(true);
      setNotificationContent({ type: 'success', message: data.message || 'Email resent successfully' });
    } catch (error) {
      setNotification(true);
      setNotificationContent({
        type: 'error',
        message: error.response?.data?.message || 'Failed to resend email',
      });
    } finally {
      setResendingId(null);
    }
  };

  const getDateLabel = (attestation) => {
    if (attestation.attestation_status === 'PENDING' && attestation.last_sent_at) {
      return `Sent: ${dateTimeParser(attestation.last_sent_at)}`;
    }
    if (attestation.attestation_date) {
      return `Date: ${dateTimeParser(attestation.attestation_date)}`;
    }
    return '';
  };

  const getStatusBadgeStyles = (status) => ({
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'uppercase',
    backgroundColor: status === 'PENDING' ? colors.PRIMARY_YELLOWISH_ORANGE : colors.GRAY_LIGHT,
    color: status === 'PENDING' ? colors.WHITE : colors.BLUE,
  });

  const attestationCardStyles = {
    padding: '16px',
    border: `1px solid ${colors.GREY}`,
    borderRadius: '8px',
    marginBottom: '12px',
    backgroundColor: colors.WHITE,
  };

  const infoRowStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px',
  };

  const actionsContainerStyles = {
    display: 'flex',
    gap: '8px',
    paddingTop: '12px',
    borderTop: `1px solid ${colors.GRAY_LIGHT}`,
  };

  const actionButtonStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '500',
    border: `1px solid ${colors.GREY}`,
    borderRadius: '6px',
    backgroundColor: colors.WHITE,
    color: colors.SECONDARY_BLACK,
    cursor: 'pointer',
    transition: 'all 0.2s',
    '&:hover': {
      backgroundColor: colors.BACKGROUND_WHITE,
      borderColor: colors.DARK_GREY,
    },
  };

  return (
    <PlainCard styles={styles.cardStyles}>
      <div css={styles.topContainerStyles}>
        <ShortText>graduate attestation</ShortText>
        <Button type="button" onClick={setModalSendAttestation}>
          <Icon name="plus" />
        </Button>
      </div>
      <div css={styles.horizontalLineStyles} />
      <div css={styles.attestationSectionContainerStyles}>
        {seekerAttestation.length === 0 ? (
          <div style={{ padding: '24px', textAlign: 'center', color: colors.DARK_GREY }}>
            No attestations yet
          </div>
        ) : (
          seekerAttestation.map((d) => {
            const url = d.link ? new URL(d.link) : null;
            const token = url?.pathname ? url.pathname.split('/attestations/')[1] : '';
            const link = token ? `/attestations/${candidateId}/${token}` : '';
            const isPending = d.attestation_status === 'PENDING';
            return (
              <div key={d.id} style={attestationCardStyles}>
                {/* Status Badge and Actions Row */}
                <div style={infoRowStyles}>
                  <div style={{ flex: 1 }}>
                    <span style={getStatusBadgeStyles(d.attestation_status)}>
                      {d.attestation_status}
                    </span>
                  </div>
                  {d.attestation_status !== 'PENDING' && (
                    <Button
                      type="button"
                      styles={styles.buttonIconStyles}
                      onClick={() => handleSelectEditAttestation(d.id)}
                      title="Edit attestation"
                    >
                      <Icon name="pen" />
                    </Button>
                  )}
                </div>

                {/* Attestation Type */}
                <div style={{ marginBottom: '8px' }}>
                  <ShortText styles={styles.attestationTypeStyles}>
                    {d.attestation_type?.toUpperCase() || 'N/A'}
                  </ShortText>
                </div>

                {/* Job Role */}
                {d.job_role && (
                  <div style={{ marginBottom: '8px' }}>
                    <ShortText style={{ fontSize: '14px', color: colors.BLUE, fontWeight: '500' }}>
                      {d.job_role}
                    </ShortText>
                  </div>
                )}

                {/* Company Name (if completed) */}
                {d.attestation_status !== 'PENDING' && d.company_name && (
                  <div style={{ marginBottom: '8px' }}>
                    <ShortText style={{ fontSize: '14px', color: colors.GRAY }}>
                      {d.company_name}
                    </ShortText>
                  </div>
                )}

                {/* Date Information */}
                <div style={{ marginBottom: '8px' }}>
                  <ShortText styles={styles.dateStyles}>
                    {getDateLabel(d)}
                  </ShortText>
                </div>

                {/* Actions - Show Open and Copy for all, Resend only for PENDING */}
                {d.link && (
                  <div style={actionsContainerStyles}>
                    <button
                      type="button"
                      onClick={() => handleOpenLink(isPending ? d.link : link)}
                      title="Open attestation link"
                      css={actionButtonStyles}
                    >
                      <Icon name="external-link-alt" />
                      <span>{d.attestation_status === 'PENDING' ? 'Open' : 'Preview'}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCopyLink(`https://career.clawnfox.com${link}`)}
                      title="Copy attestation link"
                      css={actionButtonStyles}
                    >
                      <Icon name="copy" />
                      <span>Copy</span>
                    </button>
                    {d.attestation_status === 'PENDING' && (
                      <button
                        type="button"
                        onClick={() => handleResendEmail(d.id)}
                        title="Resend attestation email"
                        css={actionButtonStyles}
                        disabled={resendingId === d.id}
                      >
                        <Icon name="envelope" />
                        <span>{resendingId === d.id ? 'Sending...' : 'Resend'}</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </PlainCard>
  );
};

AttestationSection.propTypes = {
  seekerAttestation: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setModalSendAttestation: PropTypes.func.isRequired,
  handleSelectEditAttestation: PropTypes.func.isRequired,
  fetchSeekerAttestation: PropTypes.func.isRequired,
};

export default AttestationSection;
